import React, { useEffect } from "react";
import { Form, Input, Row, Col, Checkbox, Tabs } from "antd";
import { BasicFormWrapper } from "../../../styled";

import { useMutation } from "@apollo/client";
import { UPDATE_CAR } from "../../../../graphql/AdminMutations";

import { Button } from "../../../../components/buttons/buttons";
import { SelectCompany } from "../../../../components/customSelect/selectCompany";
import { SelectCarOwner } from "../../../../components/customSelect/selectOwner";
import { SelectCarRenter } from "../../../../components/customSelect/selectRenter";
import displayNotification from "../../../reservation/admin/notification/notification";

import { CarReservations } from "./carReservations";
import { QRCodeImage } from "./qrCode";
import { CarStorage } from "./carStorage";
import { CarServiceActions } from "./carServiceActions";
import { Cards } from "../../../../components/cards/frame/cards-frame";
import { GetSelectData } from "../../newCarDataQueries";

const { TabPane } = Tabs;

const graphqlVariables = {
  tokenGet: process.env.REACT_APP_TOKEN_GET,
  tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
};

// Detail vozidla. Do 21.9.2026 to bola tretia podoba toho isteho auta, a to
// cisto na citanie: hodnoty sa vypisovali ako text, nebolo tu ani jedno
// vstupne pole, ani VIN, ani tlacidlo Ulozit. Kto sem prisiel cez SPZ alebo
// cez odkaz z Excelu, nemal odtialto kam pokracovat. Teraz sa tu da vozidlo
// priamo upravit a vedla su zalozky s jeho historiou.
const CarDetail = (props) => {
  const { t, form, selectedCar } = props;

  const ciselniky = GetSelectData(graphqlVariables, true);
  const branchOficcesData = ciselniky[0]?.data?.getBranchOficces || [];
  const carOwnersData = ciselniky[1]?.data?.getCarOwners || [];
  const carRentersData = ciselniky[2]?.data?.getCarRenters || [];

  const [triggerUpdateCarInfo, { loading: ukladam }] = useMutation(UPDATE_CAR, {
    variables: graphqlVariables,
  });

  // Data auta prichadzaju az po dopyte, preto sa polia plnia v efekte.
  useEffect(() => {
    if (selectedCar?.id) {
      form.setFieldsValue({
        licence_plate: selectedCar?.licence_plate,
        company: selectedCar?.branchOffice?.company,
        owner: selectedCar?.carOwner?.company,
        renter: selectedCar?.carRenter?.company,
        firstname: selectedCar?.driver_firstname,
        lastname: selectedCar?.driver_lastname,
        phone: selectedCar?.driver_phone,
        email: selectedCar?.driver_email,
        sendSms: Boolean(selectedCar?.sendSms),
        storage_status: Boolean(selectedCar?.storage_status),
        type: selectedCar?.type,
        vin: selectedCar?.vin,
        note: selectedCar?.note,
      });
    }
  }, [selectedCar, form]);

  // Select vracia bud id (ked clovek prepol) alebo nazov (ked nechal povodny).
  const naId = (hodnota, zoznam) =>
    isNaN(hodnota)
      ? zoznam.find(({ company }) => company === hodnota)?.id
      : hodnota;

  const ulozZmeny = (values) => {
    const variables = {
      ...graphqlVariables,
      // Mutacia vyzaduje premennu `id`, nie `carId`.
      id: parseInt(selectedCar.id),
      licencePlate: values.licence_plate,
      firstname: values.firstname,
      lastname: values.lastname,
      phone: values.phone,
      email: values.email,
      branchOffice: parseInt(naId(values.company, branchOficcesData)),
      carOwner: parseInt(naId(values.owner, carOwnersData)),
      carRenter: parseInt(naId(values.renter, carRentersData)),
      sendSms: values.sendSms || false,
      storage_status: values.storage_status || false,
      type: values.type || "",
      vin: values.vin || "",
      status: true,
      note: values.note || "",
    };

    triggerUpdateCarInfo({ variables })
      .then(() => {
        displayNotification(
          "success",
          t("backend.notifications.update"),
          "Zmeny na vozidle sú uložené."
        );
      })
      .catch(() => {
        displayNotification(
          "error",
          t("backend.notifications.update"),
          "Zmeny sa nepodarilo uložiť."
        );
      });
  };

  return (
    <Cards headless>
      <Tabs>
        <TabPane tab={t("car.tabs.tab1Title")} key="tab1">
          <BasicFormWrapper>
            <Form form={form} name="detailVozidla" onFinish={ulozZmeny}>
              <Row gutter={15}>
                <Col md={12} sm={24} xs={24}>
                  <Form.Item
                    label={t("car.licencePlate")}
                    name="licence_plate"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder={t("car.licencePlate")} />
                  </Form.Item>
                  <Form.Item name="company" label={t("car.company")}>
                    <SelectCompany data={branchOficcesData} />
                  </Form.Item>
                  <Form.Item name="owner" label={t("car.owner")}>
                    <SelectCarOwner data={carOwnersData} />
                  </Form.Item>
                  <Form.Item name="renter" label={t("car.renter")}>
                    <SelectCarRenter data={carRentersData} />
                  </Form.Item>
                  <Form.Item
                    label={t("car.sendSms")}
                    name="sendSms"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item label={t("car.type")} name="type">
                    <Input placeholder="" />
                  </Form.Item>
                </Col>
                <Col md={12} sm={24} xs={24}>
                  <Form.Item
                    label={t("car.driverFirstname")}
                    name="firstname"
                  >
                    <Input placeholder="" />
                  </Form.Item>
                  <Form.Item label={t("car.driverLastname")} name="lastname">
                    <Input placeholder="" />
                  </Form.Item>
                  <Form.Item label={t("car.driverEmail")} name="email">
                    <Input placeholder="" />
                  </Form.Item>
                  <Form.Item label={t("car.driverPhone")} name="phone">
                    <Input placeholder="+421xxxxxxxxx" autoComplete="off" />
                  </Form.Item>
                  <Form.Item
                    label={t("car.storage_status")}
                    name="storage_status"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                  <Form.Item label={t("car.vin")} name="vin">
                    <Input placeholder="" maxLength={32} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={15}>
                <Col md={24} sm={24} xs={24}>
                  <Form.Item label={t("car.note")} name="note">
                    <Input placeholder="" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={15}>
                <Col md={24} sm={24} xs={24} style={{ marginTop: "20px" }}>
                  <Button
                    htmlType="submit"
                    size="default"
                    type="primary"
                    key="ulozit"
                    disabled={ukladam}
                  >
                    {ukladam ? "Ukladám..." : t("common.saveChanges")}
                  </Button>
                </Col>
              </Row>
            </Form>
          </BasicFormWrapper>
        </TabPane>
        <TabPane tab={t("car.tabs.tab2.title")} key="tab2">
          <CarReservations t={t} carData={selectedCar} />
        </TabPane>
        <TabPane tab={t("car.tabs.tab3.title")} key="tab3">
          <QRCodeImage carData={selectedCar} />
        </TabPane>
        <TabPane tab={t("car.tabs.tab4.title")} key="tab4">
          <CarStorage t={t} carData={selectedCar} />
        </TabPane>
        <TabPane tab={t("car.tabs.tab5.title")} key="tab5">
          <CarServiceActions t={t} carData={selectedCar} />
        </TabPane>
      </Tabs>
    </Cards>
  );
};

export { CarDetail };
